import React from "react";
import { Card } from "react-bootstrap";
import CustomIcon from "./CustomIcon";
import "./StatCard.scss";

/**
 * Universal StatCard Component
 * Supports multiple diagram types and matches server response structure
 *
 * @param {string} title - Card title
 * @param {string|number} value - Main value to display
 * @param {object} trend - Trend information { type: 'positive'|'negative'|'neutral', percentage: number, text: string }
 * @param {string} variant - Card variant: 'white', 'blue', 'default'
 * @param {string} diagramType - Type of diagram: 'progress', 'line', 'bar', 'none'
 * @param {object} diagramData - Data for diagram { value: number (for progress), data: array (for charts), label: string }
 * @param {string} icon - Icon name (optional)
 * @param {string} className - Additional CSS classes
 */
const StatCard = ({
  title,
  value,
  trend = null,
  variant = "white",
  diagramType = "none",
  diagramData = {},
  icon = null,
  className = "",
  ...props
}) => {
  const getChangeIcon = () => {
    if (!trend) return "";
    switch (trend.type) {
      case "positive":
        return (
          <div className="statistic-card__info__icon positive">
            <CustomIcon name={"uparr"} size={16} />
          </div>
        );
      case "negative":
        return (
          <div className="statistic-card__info__icon negative">
            <CustomIcon name={"downarr"} size={16} />
          </div>
        );
      default:
        return (
          <div className="statistic-card__info__icon" >
            <CustomIcon name={"statup"} size={16} />
          </div>
        );
    }
  };

  const getCardClasses = () => {
    const baseClasses = ["stat-card", "h-100"];

    if (variant === "white") {
      baseClasses.push("stat-card-white");
    } else if (variant === "blue") {
      baseClasses.push("stat-card-blue");
    } else {
      baseClasses.push("stat-card-default");
    }

    if (className) {
      baseClasses.push(className);
    }

    return baseClasses.join(" ");
  };

  const getTrendClasses = () => {
    if (!trend) return "stat-change";
    return `stat-change stat-change-${trend.type}`;
  };

  const getIconClasses = () => {
    const baseClasses = ["stat-icon", "me-3"];
    if (variant === "blue") {
      baseClasses.push("stat-icon-white");
    } else {
      baseClasses.push("stat-icon-blue");
    }
    return baseClasses.join(" ");
  };

  // Generate semi-circular progress gauge
  const generateProgressGauge = (value, width = 130, height = 63) => {
    const radius = width / 2 - 10;
    const centerX = width / 2;
    const centerY = height;
    const circumference = Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (value / 100) * circumference;

    return (
      <div className="progress-gauge-container">
        <svg width={width} height={height} className="progress-gauge">
          <path
            className="progress-gauge-background"
            d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${
              centerX + radius
            } ${centerY}`}
            fill="none"
            strokeWidth="15"
            strokeLinecap="round"
          />
          <path
            className="progress-gauge-fill"
            d={`M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${
              centerX + radius
            } ${centerY}`}
            fill="none"
            strokeWidth="15"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>
        <div className="progress-gauge-label">
          {diagramData.label || `${value}%`}
        </div>
      </div>
    );
  };

  // Generate bar chart
  const generateBarChart = (data, width = 100, height = 40) => {
    if (!data || data.length === 0) return null;

    // Всегда показываем 7 столбцов
    const maxColumns = 7;
    // Берем первые 7 элементов, если данных меньше - дополняем нулями
    const chartData = [...data.slice(0, maxColumns)];
    while (chartData.length < maxColumns) {
      chartData.push(0);
    }

    const max = Math.max(...chartData, 1); // Минимум 1, чтобы избежать деления на 0
    const dataLength = maxColumns;
    const gap = 10; // Отступ между столбцами в пикселях
    const barWidth = 11; // Фиксированная ширина столбцов в пикселях

    return (
      <div className="chart-container">
        <svg width={width} height={height} className="chart-svg chart-bar">
          {chartData.map((val, index) => {
            const barHeight = max > 0 ? (val / max) * height : 0;
            const x = index * (barWidth + gap);
            const y = height - barHeight;
            const isEven = index % 2 === 0;

            return (
              <rect
                key={index}
                className={isEven ? "chart-bar-even" : "chart-bar-odd"}
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx="4"
                ry="4"
              />
            );
          })}
        </svg>
      </div>
    );
  };

  // Generate line/area chart
  const generateLineChart = (data, width = 100, height = 40, fill = false) => {
    if (!data || data.length === 0) return null;

    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;

    // Создаем плавную кривую с использованием кубических кривых Безье для более мягких переходов
    const points = data.map((value, index) => {
      const x = (index / (data.length - 1 || 1)) * width;
      const y = height - ((value - min) / range) * height;
      return { x, y };
    });

    // Строим путь с плавными кривыми Безье
    let pathData = `M ${points[0].x},${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const midX = (current.x + next.x) / 2;

      // Используем кубические кривые Безье для плавных переходов
      if (i === 0) {
        pathData += ` C ${current.x},${current.y} ${midX},${
          current.y
        } ${midX},${(current.y + next.y) / 2}`;
      }
      if (i < points.length - 1) {
        pathData += ` S ${next.x},${next.y} ${next.x},${next.y}`;
      }
    }

    // Для более плавной кривой используем упрощенный вариант с контрольными точками
    pathData = `M ${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const cp1x = prev.x + (curr.x - prev.x) / 3;
      const cp1y = prev.y;
      const cp2x = curr.x - (curr.x - prev.x) / 3;
      const cp2y = curr.y;

      if (i === 1) {
        pathData += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
      } else {
        pathData += ` S ${cp2x},${cp2y} ${curr.x},${curr.y}`;
      }
    }

    const areaPath = fill
      ? `M 0,${height} L ${points[0].x},${points[0].y} ${pathData.substring(
          1
        )} L ${width},${height} Z`
      : null;

    return (
      <div className="chart-container">
        <svg
          width={width}
          height={height}
          className={`chart-svg ${fill ? "chart-area" : "chart-line"}`}
        >
          {fill && (
            <path
              className="chart-area-fill"
              d={areaPath}
              fill="rgba(255, 255, 255, 0.2)"
            />
          )}
          <path
            className="chart-line-path"
            d={pathData}
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    );
  };

  const renderDiagram = () => {
    switch (diagramType) {
      case "progress":
        return generateProgressGauge(
          diagramData.value || 0,
          diagramData.width || 130,
          diagramData.height || 63
        );

      case "bar":
        return generateBarChart(
          diagramData.data || [],
          diagramData.width || 100,
          diagramData.height || 40
        );

      case "line":
        return generateLineChart(
          diagramData.data || [],
          diagramData.width || 100,
          diagramData.height || 40,
          false
        );

      case "area":
        return generateLineChart(
          diagramData.data || [],
          diagramData.width || 100,
          diagramData.height || 40,
          true
        );

      default:
        return null;
    }
  };

  return (
    <Card className={getCardClasses()} {...props}>
      <Card.Body className="statistic-card-body ">
        <div className="statistic-card__info">
          <div className="flex-grow-1 statistic-card__wrapper">
            <div className="d-flex align-items-center mb-2">
              {icon && (
                <div className={getIconClasses()}>
                  <CustomIcon name={icon} size={24} />
                </div>
              )}
              <div>
                <h6 className="stat-title">{title}</h6>
              </div>
            </div>

            <h3 className="stat-value">{value}</h3>

            {trend && (
              <div className={getTrendClasses()}>
                <span className="stat-change-icon">{getChangeIcon()}</span>
                <span className="stat-change-text">
                  {trend.percentage !== undefined &&
                    `${Math.abs(trend.percentage)}%`}{" "}
                  {trend.text || ""}
                </span>
              </div>
            )}
          </div>

          {diagramType !== "none" && (
            <div className="chart-section">{renderDiagram()}</div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default StatCard;
